import React, { FunctionComponent } from 'react'
import Grid from '@sport1/react-elements/Grid'
import FontelloIcon from '@sport1/react-elements/FontelloIcon'
import Touchable from '@sport1/react-elements/Touchable'
import { TestID } from '../../../../utils/PropTypesDefinitions'
import { RedCard, SecondYellowCard, YellowCard } from '../../HelperClass/Icons'
import Label10Font from '../../../../Theme/Fonts/Label10'
import PlayerImage from '../../../../BaseComponents/Image/Player'

export type WarningCard = 'RED_CARD' | 'YELLOW_CARD' | 'SECOND_YELLOW_CARD'
export type TeamPlayerProps = {
    /**
     * players name, in format of FirstName LastName
     */
    playerName: string
    /**
     * players Image Url,to be shown in left corner
     */
    playerImageUrl?: string
    /**
     * team specific color ,to be shown in background of jersey number
     */
    teamColor: string
    /**
     * the number allocated to the player
     */
    jerseyNumber?: string
    /**
     * warning Card for player
     */
    warningCardList?: WarningCard[] | WarningCard
    /**
     * the url to special logo, showing needed additional info in right top corner of player image
     */
    additionalInfoIconName?: string
    /**
     * number of goals made by player
     */
    goalNumber?: number
    /**
     * adjustable paddingTop for whole component
     */
    paddingTop?: number
    /**
     * adjustable paddingBottom for whole component to support Drop shadow
     */
    paddingBottom?: number
    /**
     * adjustable paddingX for whole component
     */
    paddingX?: number
    /**
     * action called when pressed on the whole row
     * @TJS-type: function
     */
    onPressPlayer: () => void
} & TestID

const getCard = (warningCardList: WarningCard[] | string) => {
    const warningCards = Array.isArray(warningCardList) ? warningCardList : [warningCardList]
    const result = warningCards.map((card, index) => {
        if (card === 'RED_CARD') return <RedCard fontSize="FS12" />
        if (card === 'YELLOW_CARD') return <YellowCard fontSize="FS12" />
        return <SecondYellowCard key={`YellowCard${index}`} fontSize="FS12" />
    })

    return result.map((card, index) => {
        return (
            <Grid key={`${warningCardList}_${index}`} noExpand marginRight="1px">
                {card}
            </Grid>
        )
    })
}

const getColorForJerseyNumber = (teamColor: string): string => {
    const colorValues = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(teamColor)

    if (colorValues && colorValues.length === 4) {
        const [r, g, b] = [colorValues[1], colorValues[2], colorValues[3]].map(color =>
            parseInt(color, 16)
        )
        const rgbBrightness = Math.sqrt(0.299 * r ** 2 + 0.587 * g ** 2 + 0.114 * b ** 2)
        return rgbBrightness > 150 ? 'black' : 'white'
    }

    return 'white'
}
const getIconName = (goalNumber: number) => {
    if (goalNumber > 9) return '9goalplus'
    return `${goalNumber}goal`
}

const TeamPlayer: FunctionComponent<TeamPlayerProps> = ({
    playerImageUrl,
    playerName,
    jerseyNumber,
    onPressPlayer,
    goalNumber,
    additionalInfoIconName,
    warningCardList,
    teamColor,
    testID,
    paddingX,
    paddingBottom,
    paddingTop,
}) => {
    return (
        <Grid
            paddingBottom={paddingBottom}
            paddingTop={paddingTop}
            paddingX={paddingX}
            centerHorizontal
            testID={testID}
        >
            <Touchable expand onPress={onPressPlayer}>
                <Grid noExpand height={goalNumber || warningCardList ? undefined : 65}>
                    <Grid horizontal centerHorizontal noExpand>
                        <Grid
                            centerHorizontal
                            elevation={10}
                            zIndex={-1}
                            marginBottom={-2}
                            borderRadius={8}
                            noExpand
                            position="relative"
                        >
                            <PlayerImage
                                imageUrl={playerImageUrl}
                                alt={`player-${playerName}-image`}
                                width={32}
                                size="S"
                            />

                            {additionalInfoIconName && (
                                <Grid width={17} position="absolute" left={35} top={0}>
                                    <FontelloIcon
                                        textAlign="left"
                                        name={additionalInfoIconName}
                                        fontSize={12}
                                        color="white"
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Grid height={16} noExpand centerHorizontal horizontal>
                        {jerseyNumber ? (
                            <Grid width={14} noExpand backgroundColor={teamColor} elevation={16}>
                                <Label10Font
                                    textAlign="center"
                                    color={getColorForJerseyNumber(teamColor)}
                                >
                                    {jerseyNumber}
                                </Label10Font>
                            </Grid>
                        ) : null}
                        <Grid
                            noExpand
                            paddingX={2}
                            paddingLeft={14}
                            backgroundColor="black"
                            elevation={16}
                            minWidth={0}
                        >
                            <Label10Font textAlign="center" color="white" numberOfLines={1}>
                                {playerName}
                            </Label10Font>
                        </Grid>
                    </Grid>
                </Grid>
                {warningCardList || goalNumber ? (
                    <Grid noExpand marginTop={1} horizontal center>
                        {warningCardList && getCard(warningCardList)}
                        {goalNumber && (
                            <Grid noExpand width={13} height={14} overflow="visible" center>
                                <Grid
                                    width={12}
                                    height={12}
                                    noExpand
                                    borderRadius={6}
                                    center
                                    backgroundColor="white"
                                />
                                <Grid position="absolute" paddingRight={!warningCardList && '1px'}>
                                    <FontelloIcon name={getIconName(goalNumber)} fontSize={12} />
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                ) : undefined}
            </Touchable>
        </Grid>
    )
}
export default TeamPlayer
